import React, { useState, useEffect } from "react";
import { Link, Redirect, useLocation, useHistory } from "react-router-dom";
import styles from "./index.module.css";
import { Button, Form, Grid, Loader } from "semantic-ui-react";
import agent from "../../../agent";
import { Institution } from "../../dashboard";
import { GeneralTitleCase, getGeneralTitle } from "../../../utils/common";
import { useReturnUrl } from "../../../hooks/useReturnUrl";
import { textLabels } from "../../../consts/textLabels";

interface MatchParams {
    id: string;
    [key: string]: string;
}

interface InstitutionEditorState {
    editable: boolean;
    _id: string;
    name: string;
    link?: string;
    active: boolean;
    loading: boolean;
    saved: boolean;
    editMode: boolean;
    returnUrl: string; // Сохраняем URL для возврата
}

const InstitutionEditor: React.FC = () => {
    const location = useLocation();
    const history = useHistory();
    const FALLBACK_URL = "/dashboard/institutions";
    const { returnUrl, handleGoBackTo } = useReturnUrl(FALLBACK_URL);

    const [state, setState] = useState<InstitutionEditorState>({
        editable: false,
        _id: "",
        name: "",
        link: "",
        active: true,
        loading: false,
        saved: false,
        editMode: false,
        returnUrl,
    });

    const isEditMode = location.pathname.includes("/edit");

    useEffect(() => {
        setState(prev => ({ ...prev, returnUrl }));

        if (isEditMode) {
            setState(prev => ({ ...prev, editMode: true, loading: true }));
            loadInstitution();
        } else {
            setState(prev => ({ ...prev, editable: true }));
        }
    }, [isEditMode, returnUrl]);

    const loadInstitution = async () => {
        const pathParts = location.pathname.split("/");
        const idIndex = pathParts.indexOf("institutions") + 1;
        const institutionId = pathParts[idIndex];

        if (!institutionId) return;

        try {
            const doc: Institution = await agent.InstitutionProvider.open(institutionId);
            if (doc) {
                setState({
                    _id: doc._id,
                    name: doc.name,
                    link: doc.link || "",
                    active: doc.active,
                    loading: false,
                    editable: false,
                    editMode: true,
                    saved: false,
                    returnUrl: state.returnUrl,
                });
            }
        } catch (error) {
            console.error("Ошибка загрузки:", error);
            setState(prev => ({ ...prev, loading: false }));
        }
    };

    const onEdit = async () => {
        setState(prev => ({ ...prev, loading: true }));

        try {
            const doc: Institution = await agent.InstitutionProvider.edit({
                _id: state._id,
                name: state.name,
                link: state.link,
                active: state.active,
            });

            setState({
                _id: doc._id,
                name: doc.name,
                link: doc.link || "",
                active: doc.active,
                editable: false,
                editMode: true,
                saved: true,
                loading: false,
                returnUrl: state.returnUrl,
            });
        } catch (error) {
            console.error("Ошибка сохранения:", error);
            setState(prev => ({ ...prev, loading: false }));
        }
    };

    const isSaveDisabled = (): boolean => {
        return !state.name.trim();
    };

    const handleGoBack = (e: React.MouseEvent) => {
        e.preventDefault();
        handleGoBackTo();
    };

    if (state.saved) {
        return <Redirect to={state.returnUrl} />;
    }

    const entityLabel = getGeneralTitle(GeneralTitleCase.showInstitutionsButtonBot);
    const headerLabel = `Данные медицинского учреждения`;

    return (
        <div className={styles.institutionInfoContent}>
            <div className={styles.institutionInfoHeader}>
                {headerLabel}
            </div>

            {state.loading ? (
                <Loader active />
            ) : (
                <Form>
                    <Grid>
                        <Grid.Column width={4}>
                            <Form.Input
                                label="Наименование"
                                value={state.name}
                                readOnly
                                disabled={!state.editable}
                                onChange={(e) =>
                                    setState(prev => ({ ...prev, name: e.target.value }))
                                }
                            />
                            <Form.Input
                                label="Ссылка"
                                value={state.link || ""}
                                readOnly
                                disabled={!state.editable}
                                onChange={(e) =>
                                    setState(prev => ({ ...prev, link: e.target.value }))
                                }
                            />
                            <Form.Checkbox
                                label="Активность кнопки в боте"
                                checked={state.active}
                                disabled={!state.editable}
                                onChange={(event, data) =>
                                    setState(prev => ({ ...prev, active: Boolean(data.checked) }))
                                }
                            />
                        </Grid.Column>

                        <Grid.Column width={3}>
                            <div className={styles.institutionEditorButtons}>
                                {state.editable ? (
                                    <Button
                                        className={styles.button}
                                        color="green"
                                        type="submit"
                                        content={textLabels.saveButton}
                                        disabled={isSaveDisabled()}
                                        onClick={onEdit}
                                    />
                                ) : (
                                    <Button
                                        className={styles.button}
                                        color="teal"
                                        type="submit"
                                        content={textLabels.editButton}
                                        onClick={() => setState(prev => ({ ...prev, editable: true }))}
                                    />
                                )}

                                <Button
                                    className={styles.button}
                                    color="grey"
                                    type="button"
                                    content={textLabels.returnButton}
                                    onClick={handleGoBack}
                                />
                            </div>
                        </Grid.Column>
                    </Grid>
                </Form>
            )}
        </div>
    );
};

export default InstitutionEditor;